import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Group,
  Menu,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { ChartCard } from '../../../components/ChartCard';
import { DateRangeSelect } from '../../../components/DateRangeSelect';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { UnifyTable, type UnifyTableColumn } from '../../../components/UnifyTable';
import { UnifyChartTooltip, UnifyLineChart } from '../../../components/charts';
import { DatePickerInput, DateTimePicker } from '@mantine/dates';
import { IconBan, IconCalendarEvent, IconDots, IconPlayerPlay, IconPlus, IconPencil } from '@tabler/icons-react';
import { FEATURE_VISIBILITY } from '../../../config/featureVisibility';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CodeableConceptDisplay,
  useMedplum,
} from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { getStatusColor, formatStatusText } from '../../../lib/statusUtils';
import type { Questionnaire, Reference, Task } from '@medplum/fhirtypes';
import { useResolvedTemplates } from '../../settings/template-library/hooks';
import { getScoreRanges } from '../../settings/template-library/utils/templateTags';
import type { Assessment, HealthMetric } from '../data';
import { toEpisodeSelectOptions, type EpisodePickerItem } from '../data/episodeSelectOptions';
import {
  buildAssessmentTrendDataPoints,
  enrichAssessmentTrendTooltipPayload,
  getQuestionnaireResponseIdFromTask,
  maxScoreByAssessmentType,
  normalizeAssessmentTrendDataPoints,
  resolveAssessmentScore,
  resolveMaxScoreForQuestionnaire,
  sumQuestionnaireResponseItemValues,
} from '../../../lib/assessmentMedplumAdapters';
import { getAssessmentCodeableConcept } from '../../../lib/assessmentToFhir';
import {
  buildAssessmentAssignmentTask,
  buildAssessmentOccurrenceTask,
  buildProviderTakeNowTask,
  getPatientAccountRefs,
  getTaskEpisodeRef,
  isAssessmentOccurrenceTask,
  isFormRequestTask,
  isRecurringStandingOrder,
  isScheduledAssessmentTask,
  isTakeNowAssessmentTask,
} from '../../../lib/clientFacingTasks';
import { executePortalTaskLifecycle, executeTaskMutation } from '../../../lib/taskLifecycleBots';
import type { EpisodeChartInteraction } from '../../../hooks/useEpisodeScopedClientChartBundle';
import { getAssessmentChartAccess } from '../../../lib/rbac/eocChartScope';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import {
  executeActivateSchedule,
  executeCancelAssessment,
  executeStopSchedule,
} from '../../../lib/assessmentLifecycleService';
import {
  clearAssessmentSchedule,
  getAssessmentCancellationReason,
  getAssessmentFrequency,
  getAssessmentRecurrenceEnd,
  setAssessmentSchedule,
  type RecurringAssessmentFrequency,
} from '@unify/fhir-model/resources/assessment-task';
import { formatMedplumClientError } from '../../appointments/utils/medplumClientErrors';
import { dateInputValueToDateOnly, formatDateOnlyLocal } from '../../../lib/dateHelpers';
import {
  ASSESSMENT_FREQUENCIES,
  calculateNextAssessmentSendDate,
  isRecurringFrequency,
  type AssessmentFrequency,
} from '../../../lib/assessmentRecurrence';
import { isMeasuresQuestionnaire } from '../../../lib/assessmentMedplumAdapters';
import { resolveTaskRequesterLabel } from '../../../lib/taskAssignerDisplay';
import { patientCompartmentSearch } from '../../../lib/patientCoverageSearch';
import { stablePaletteColorForKey } from '@/pages/design-system/pages/foundations/chartPalette';

const FREQUENCY_SELECT_DATA = ASSESSMENT_FREQUENCIES.filter((f) => f !== 'Every Session').map((f) => ({
  value: f,
  label: f,
}));

/** Mock assessment type options when Medplum has no Measures templates (for Add Assignment and Add Assessment modals). */
const MOCK_ASSESSMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'PHQ-9', label: 'PHQ-9 (Depression)' },
  { value: 'GAD-7', label: 'GAD-7 (Anxiety)' },
  { value: 'PCL-5', label: 'PCL-5 (PTSD)' },
  { value: 'WAI', label: 'WAI (Alliance)' },
];

/** Maps mock assessment type values to mock template IDs used in getMockTemplates(). */
const MOCK_TYPE_TO_TEMPLATE_ID: Record<string, string> = {
  'PHQ-9': 'mock-measure-1',
  'GAD-7': 'mock-measure-2',
  'PCL-5': 'mock-measure-1',
  'WAI': 'mock-measure-2',
};

/** One row in the Assignments panel: Type, Frequency, End Date, Last submission, Score, Status. */
interface AssignmentRow {
  id: string;
  type: string;
  frequency: string;
  /** YYYY-MM-DD recurrence end date, if set. */
  endDate?: string;
  lastSubmission: string;
  score: number;
  maxScore: number;
  status: string;
  /** Original assessment for navigation/response when needed */
  assessment?: Assessment;
}

/** Dummy assignment rows when Medplum is unavailable or returns empty. One row per assessment type. */
const DUMMY_ASSIGNMENTS: AssignmentRow[] = [
  { id: 'dummy-a1', type: 'PHQ-9', frequency: 'Weekly', lastSubmission: '2026-02-15', score: 4, maxScore: 27, status: 'In Progress' },
  { id: 'dummy-a2', type: 'GAD-7', frequency: 'Biweekly', lastSubmission: '2026-02-10', score: 3, maxScore: 21, status: 'In Progress' },
  { id: 'dummy-a3', type: 'PCL-5', frequency: 'Monthly', lastSubmission: '2026-01-20', score: 22, maxScore: 80, status: 'Completed' },
];

/** Dummy assessments for log when Medplum is unavailable or returns empty. */
const DUMMY_LOG_ASSESSMENTS: Assessment[] = [
  { id: 'dummy-l1', assessmentType: 'PHQ-9', date: '2026-02-15', time: '', provider: 'Dr. Chen', score: 4, maxScore: 27, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l2', assessmentType: 'GAD-7', date: '2026-02-10', time: '', provider: 'Dr. Chen', score: 3, maxScore: 21, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l3', assessmentType: 'PCL-5', date: '2026-01-20', time: '', provider: 'Dr. Torres', score: 22, maxScore: 80, administeredBy: 'Dr. Torres', status: 'Completed' },
  { id: 'dummy-l4', assessmentType: 'WAI', date: '2026-01-15', time: '', provider: 'Dr. Chen', score: 72, maxScore: 84, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l5', assessmentType: 'PHQ-9', date: '2026-02-08', time: '', provider: 'Dr. Chen', score: 6, maxScore: 27, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l6', assessmentType: 'GAD-7', date: '2026-02-05', time: '', provider: 'Dr. Torres', score: 8, maxScore: 21, administeredBy: 'Dr. Torres', status: 'Completed' },
  { id: 'dummy-l7', assessmentType: 'PHQ-9', date: '2026-01-28', time: '', provider: 'Dr. Chen', score: 10, maxScore: 27, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l8', assessmentType: 'WAI', date: '2026-01-22', time: '', provider: 'Dr. Chen', score: 68, maxScore: 84, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l9', assessmentType: 'PCL-5', date: '2026-01-18', time: '', provider: 'Dr. Torres', score: 18, maxScore: 80, administeredBy: 'Dr. Torres', status: 'Completed' },
  { id: 'dummy-l10', assessmentType: 'GAD-7', date: '2026-01-12', time: '', provider: 'Dr. Chen', score: 5, maxScore: 21, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l11', assessmentType: 'PHQ-9', date: '2026-01-05', time: '', provider: 'Dr. Torres', score: 12, maxScore: 27, administeredBy: 'Dr. Torres', status: 'Completed' },
  { id: 'dummy-l12', assessmentType: 'WAI', date: '2025-12-28', time: '', provider: 'Dr. Chen', score: 75, maxScore: 84, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l13', assessmentType: 'PCL-5', date: '2025-12-20', time: '', provider: 'Dr. Torres', score: 28, maxScore: 80, administeredBy: 'Dr. Torres', status: 'Completed' },
  { id: 'dummy-l14', assessmentType: 'PHQ-9', date: '2025-12-15', time: '', provider: 'Dr. Chen', score: 14, maxScore: 27, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l15', assessmentType: 'GAD-7', date: '2025-12-08', time: '', provider: 'Dr. Chen', score: 11, maxScore: 21, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l16', assessmentType: 'WAI', date: '2025-12-01', time: '', provider: 'Dr. Torres', score: 70, maxScore: 84, administeredBy: 'Dr. Torres', status: 'Completed' },
  { id: 'dummy-l17', assessmentType: 'PHQ-9', date: '2025-11-24', time: '', provider: 'Dr. Chen', score: 16, maxScore: 27, administeredBy: 'Dr. Chen', status: 'Completed' },
  { id: 'dummy-l18', assessmentType: 'PCL-5', date: '2025-11-18', time: '', provider: 'Dr. Torres', score: 35, maxScore: 80, administeredBy: 'Dr. Torres', status: 'Completed' },
];

interface MeasuresTabProps {
  /** Client chart patient id (route / Medplum Patient id). */
  patientId: string;
  assessments: Assessment[];
  healthMetrics: HealthMetric[];
  /** Active episodes for episode-scoped assignment. */
  episodes?: EpisodePickerItem[];
  /** Authorized episode IDs for the current provider (filters EoC dropdowns). */
  authorizedEpisodeIds?: ReadonlySet<string>;
  /** When set, assessment log rows outside authorized episodes are listed but not navigable (same as medical record). */
  episodeInteraction?: EpisodeChartInteraction;
  /** Called after a Task is created/updated to refresh chart data. */
  onRefetch?: () => void;
}

/** Format a Date as "May 7, 2026 at 2:30 PM". */
function formatDateTimeFriendly(d: Date): string {
  const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} at ${timePart}`;
}

/** `DateTimePicker` onChange may pass a `Date`, ISO string, timestamp, or null — normalize before storing ISO. */
function dateTimePickerValueToIsoStored(v: unknown): string {
  if (v == null) return '';
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? '' : v.toISOString();
  }
  const d = new Date(v as string | number);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** True if string is YYYY-MM-DD (calendar date; avoid UTC midnight parse shift). */
function isIsoDateOnlyString(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s.trim());
}

/**
 * Format date for assessments log. Date-only strings use local calendar day; full ISO uses that instant.
 */
function formatDate(dateString: string): string {
  if (!dateString || dateString === '--') return '--';
  const t = dateString.trim();
  let d: Date;
  if (isIsoDateOnlyString(t)) {
    const [y, m, day] = t.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = new Date(t);
  }
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US');
}

/** Get YYYY-MM-DD from an assessment date (calendar or ISO instant — local calendar day). */
function toIsoDateOnly(dateStr: string): string | null {
  if (!dateStr?.trim()) return null;
  const t = dateStr.trim();
  if (isIsoDateOnlyString(t)) return t;
  try {
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch {
    return null;
  }
}

function assessmentDateSortValue(dateStr: string): number {
  const t = dateStr?.trim() ?? '';
  if (!t) return 0;
  if (isIsoDateOnlyString(t)) {
    const [y, m, day] = t.split('-').map(Number);
    return new Date(y, m - 1, day).getTime();
  }
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Score badge color: red (bad), yellow (ok), green (good).
 * If template has score ranges (from Template Library), use those; otherwise use ratio (higher = more severe).
 */
function getScoreBadgeColor(
  score: number,
  maxScore: number,
  template?: Questionnaire | null,
): 'red' | 'yellow' | 'green' {
  if (maxScore <= 0) return 'green';
  const ranges = template ? getScoreRanges(template) : undefined;
  const { goodMax, okMax } = ranges ?? {};
  if (
    typeof goodMax === 'number' &&
    typeof okMax === 'number' &&
    goodMax < okMax
  ) {
    if (score <= goodMax) return 'green';
    if (score <= okMax) return 'yellow';
    return 'red';
  }
  const ratio = score / maxScore;
  if (ratio > 0.5) return 'red';
  if (ratio > 0.25) return 'yellow';
  return 'green';
}

/** Find a scored-measure template whose title matches the assessment type (e.g. "PHQ-9"). */
function findTemplateByAssessmentType(
  templates: Questionnaire[],
  assessmentType: string,
): Questionnaire | undefined {
  const normalized = assessmentType.trim();
  return templates.find(
    (q) => (q.title ?? '').split('(')[0].trim() === normalized,
  );
}

/** Type badge colors from StylingPage Tag palette, excluding Red/Yellow/Green/Gray: Blue, Pink, Purple, Orange, Cyan, Teal. */
const TYPE_BADGE_COLOR_CYCLE = ['blue', 'pink', 'violet', 'orange', 'cyan', 'teal'] as const;
type TypeBadgeColor = (typeof TYPE_BADGE_COLOR_CYCLE)[number];

/** Assign a stable color to an assessment type using the StylingPage color cycle. */
function getAssessmentTypeBadgeColor(type: string): TypeBadgeColor {
  const knownOrder = ['PHQ-9', 'GAD-7', 'PCL-5', 'WAI'];
  const index = knownOrder.indexOf(type);
  if (index >= 0) return TYPE_BADGE_COLOR_CYCLE[index % TYPE_BADGE_COLOR_CYCLE.length];
  const hash = type.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return TYPE_BADGE_COLOR_CYCLE[Math.abs(hash) % TYPE_BADGE_COLOR_CYCLE.length];
}

/**
 * Measures tab showing assessment trends and health metrics
 */
export function MeasuresTab({
  patientId,
  assessments: chartAssessments,
  healthMetrics,
  episodes,
  authorizedEpisodeIds,
  episodeInteraction,
  onRefetch,
}: MeasuresTabProps): JSX.Element {
  const medplum = useMedplum();
  const { practitioner } = useCurrentUserPractitioner();
  const { id: clientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [taskAssessmentsFromApi, setTaskAssessmentsFromApi] = useState<Assessment[]>([]);
  const [assessmentTypeFilter, setAssessmentTypeFilter] = useState<string[]>([
    'PHQ-9',
    'GAD-7',
    'PCL-5',
    'WAI',
  ]);
  const [assessmentSortDirection] = useState<'asc' | 'desc'>('desc');
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [editingAssessmentId, setEditingAssessmentId] = useState<string | null>(null);
  const [assessmentForm, setAssessmentForm] = useState<Assessment>({
    id: '',
    assessmentType: 'PHQ-9',
    date: '',
    time: '',
    provider: '',
    score: 0,
    maxScore: 27,
    severity: '',
    notes: '',
    administeredBy: '',
    status: 'Completed',
  });
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  const [modifyingAssignment, setModifyingAssignment] = useState<AssignmentRow | null>(null);
  const [modifyAssignmentForm, setModifyAssignmentForm] = useState<{
    frequency: string;
    endDate: string;
    notes: string;
  }>({ frequency: 'Weekly', endDate: '', notes: '' });
  const [modifyLoading, setModifyLoading] = useState(false);
  const [stopScheduleConfirmRow, setStopScheduleConfirmRow] = useState<AssignmentRow | null>(null);
  const [stopScheduleLoading, setStopScheduleLoading] = useState(false);
  const [activateScheduleRowId, setActivateScheduleRowId] = useState<string | null>(null);
  const [cancelAssessmentConfirm, setCancelAssessmentConfirm] = useState<Assessment | null>(null);
  const [cancelAssessmentReason, setCancelAssessmentReason] = useState('Assigned in error');
  const [cancelAssessmentLoading, setCancelAssessmentLoading] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState<{
    selectedQuestionnaireId: string;
    frequency: string;
    notes: string;
    startDate: string;
    endDate: string;
    dueDate: string;
    episodeId: string;
  }>({
    selectedQuestionnaireId: '',
    frequency: 'Once',
    notes: '',
    startDate: '',
    endDate: '',
    dueDate: '',
    episodeId: '',
  });
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const { templates: resolvedMeasureTemplates, refetch: refetchMeasureTemplates } = useResolvedTemplates({
    target: 'assessment',
    category: 'measures',
  });
  const scoredMeasureTemplates = resolvedMeasureTemplates;
  const [, setMedplumErrorOrEmpty] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<string[]>([]);
  const [logDateRange, setLogDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const assessmentsLogSectionRef = useRef<HTMLDivElement>(null);
  const [addAssessmentModalOpen, setAddAssessmentModalOpen] = useState(false);
  const [addAssessmentForm, setAddAssessmentForm] = useState<{
    selectedQuestionnaireId: string;
    dueDate: string;
  }>({ selectedQuestionnaireId: '', dueDate: '' });
  const [addAssessmentLoading, setAddAssessmentLoading] = useState(false);
  /** Locally completed assessments (from "Take Now" mock flow returning to Measures). */
  const [localCompletedAssessments, setLocalCompletedAssessments] = useState<Assessment[]>([]);
  const [patientAccountRefs, setPatientAccountRefs] = useState<Reference[]>([]);
  const [addAssessmentEpisodeId, setAddAssessmentEpisodeId] = useState<string>('');

  const accessibleEpisodes = useMemo(() => {
    if (!episodes) return [];
    if (!authorizedEpisodeIds) return episodes;
    return episodes.filter((e) => authorizedEpisodeIds.has(e.id));
  }, [episodes, authorizedEpisodeIds]);

  const getAssessmentAccess = useCallback(
    (assessment: Assessment) => {
      if (!episodeInteraction) return 'full' as const;
      return getAssessmentChartAccess(
        assessment.episodeId,
        episodeInteraction.authorizedEpisodeIds,
        episodeInteraction.patientEpisodeCount,
        episodeInteraction.viewScope,
      );
    },
    [episodeInteraction],
  );

  useEffect(() => {
    if (!patientId) return;
    getPatientAccountRefs(medplum, patientId)
      .then(setPatientAccountRefs)
      .catch(() => {});
  }, [medplum, patientId]);

  const defaultMaxScoreMap: Record<string, number> = useMemo(
    () => ({ 'PHQ-9': 27, 'GAD-7': 21, 'PCL-5': 80, WAI: 84 }),
    [],
  );
  const resolveMaxScore = useCallback(
    (type: string) => defaultMaxScoreMap[type] ?? 100,
    [defaultMaxScoreMap],
  );

  const fetchScoredMeasureTemplates = useCallback(() => {
    refetchMeasureTemplates();
  }, [refetchMeasureTemplates]);

  const measuresTemplateSelectData = useMemo(
    () =>
      scoredMeasureTemplates.map((q) => ({
        value: q.id!,
        label: q.title ?? q.name ?? q.id!,
      })),
    [scoredMeasureTemplates],
  );

  const fetchTaskAssessments = useCallback(async () => {
    try {
      const patientRef = `Patient/${patientId}`;
      const compartment = patientCompartmentSearch(patientId);
      const taskForPatient = (t: Task) => t.for?.reference === patientRef;
      const [completedTasks, requestedTasks, onHoldTasks, cancelledTasks] = await Promise.all([
        medplum
          .searchResources('Task', { ...compartment, status: 'completed', _count: '200' })
          .then((rows) => rows.filter(taskForPatient)),
        medplum
          .searchResources('Task', { ...compartment, status: 'requested', _count: '200' })
          .then((rows) => rows.filter(taskForPatient)),
        medplum
          .searchResources('Task', { ...compartment, status: 'on-hold', _count: '200' })
          .then((rows) => rows.filter(taskForPatient)),
        medplum
          .searchResources('Task', { ...compartment, status: 'cancelled', _count: '200' })
          .then((rows) => rows.filter(taskForPatient)),
      ]);
      const allTasks = [...requestedTasks, ...onHoldTasks, ...completedTasks, ...cancelledTasks].filter((t) =>
        t.focus?.reference?.startsWith('Questionnaire/'),
      );
      const taskAssessments: Assessment[] = [];
      const assignerLabelCache = new Map<string, string>();

      for (const task of allTasks) {
        const qRef = task.focus?.reference;
        if (!qRef) continue;
        let qTitle = task.description ?? '';
        let questionnaire: Questionnaire | null = null;
        try {
          const [rt, qId] = qRef.split('/');
          if (rt === 'Questionnaire' && qId) {
            questionnaire = await medplum.readResource('Questionnaire', qId);
            qTitle = questionnaire.title ?? qTitle;
          }
        } catch { /* ignore */ }

        // Exclude form requests from the Assessment Log.
        if (isFormRequestTask(task)) continue;
        if (isTakeNowAssessmentTask(task)) {
          // Take Now tasks only appear when completed or cancelled (not while in-progress fill).
          if (task.status !== 'completed' && task.status !== 'cancelled') continue;
        }
        if (questionnaire && !isMeasuresQuestionnaire(questionnaire)) continue;

        // Recurring standing orders (the schedule config task with frequency extension) must
        // NOT appear in the Assessment Log — only occurrence / one-time Tasks appear there.
        if (isRecurringStandingOrder(task)) continue;
        // Deferred Once sends stay in Schedule Assessments until cron promotes them.
        if (task.status === 'on-hold') continue;

        const assessmentType = qTitle.split('(')[0]?.trim() || qTitle;
        const isRequested = task.status === 'requested';
        const isCancelled = task.status === 'cancelled';
        let score = 0;
        let maxScore = questionnaire
          ? resolveMaxScoreForQuestionnaire(questionnaire)
          : resolveMaxScore(assessmentType);
        let dateStr = task.lastModified ?? task.authoredOn ?? '';
        let rowStatus: Assessment['status'] = isCancelled
          ? 'Cancelled'
          : isRequested
            ? 'In Progress'
            : 'Completed';
        let cancellationReason: string | undefined;
        let cancelledAt: string | undefined;

        if (isCancelled) {
          const reason = getAssessmentCancellationReason(task);
          cancellationReason = reason?.text ?? reason?.coding?.[0]?.display;
          cancelledAt = task.lastModified ?? task.meta?.lastUpdated;
          dateStr = cancelledAt ?? dateStr;
        }

        if (task.status === 'completed' && questionnaire) {
          const qrId = getQuestionnaireResponseIdFromTask(task);
          if (qrId) {
            try {
              const qr = await medplum.readResource('QuestionnaireResponse', qrId);
              const resolved = resolveAssessmentScore(qr, questionnaire, undefined);
              score = resolved.score;
              maxScore = resolveMaxScoreForQuestionnaire(questionnaire);
              dateStr = qr.authored ?? qr.meta?.lastUpdated ?? dateStr;
              rowStatus = qr.status === 'completed' ? 'Completed' : 'In Progress';
            } catch {
              /* keep task-level defaults */
            }
          }
        }

        const administeredBy = await resolveTaskRequesterLabel(medplum, task.requester, assignerLabelCache);
        const epRef = getTaskEpisodeRef(task);
        const episodeIdFromTask =
          epRef?.startsWith('EpisodeOfCare/') ? epRef.replace('EpisodeOfCare/', '') : undefined;

        taskAssessments.push({
          id: `task-${task.id}`,
          assessmentTaskId: task.id,
          assessmentType,
          date: dateStr,
          time: '',
          provider: task.requester?.display ?? administeredBy,
          score: isCancelled ? 0 : score,
          maxScore,
          severity: '',
          notes: task.description ?? '',
          administeredBy,
          status: rowStatus,
          requestedDate: task.authoredOn,
          dueDate: task.restriction?.period?.end,
          ...(cancellationReason ? { cancellationReason } : {}),
          ...(cancelledAt ? { cancelledAt } : {}),
          ...(episodeIdFromTask ? { episodeId: episodeIdFromTask } : {}),
        });
      }

      setTaskAssessmentsFromApi(taskAssessments);
      setMedplumErrorOrEmpty(taskAssessments.length === 0);

      // ── Schedule Assessments panel ────────────────────────────────────────
      // Shows schedule-series resources only:
      //   • Standing orders (isRecurringStandingOrder) — always shown while active/stopped
      //   • One-time "Once" scheduled assessments (no frequency ext, no partOf link)
      //
      // Excluded from the schedule panel:
      //   • Occurrence Tasks (have partOf → standing order Task) — shown in Assessment Log
      //   • Take Now Tasks
      //   • Form Request Tasks
      //
      // Both requested (active) and completed (done/history) tasks are included so
      // completed once-type assignments do not disappear from the panel.

      // Build a map of questionnaire ref → last completed QR (date + score) for
      // populating the "Date of last submission" and "Score" columns.
      const qrByQuestionnaire = new Map<string, { authored: string; score: number }>();
      for (const ct of [...completedTasks, ...requestedTasks]) {
        if (!ct.focus?.reference?.startsWith('Questionnaire/')) continue;
        const qKey = ct.focus.reference;
        const qrId = getQuestionnaireResponseIdFromTask(ct);
        if (!qrId) continue;
        try {
          const qr = await medplum.readResource('QuestionnaireResponse', qrId);
          const qMatch = scoredMeasureTemplates.find((q) => `Questionnaire/${q.id}` === qKey);
          // Observation lookup deferred to the Scoring Bot; fall back to sum here
          const resolved = qMatch
            ? resolveAssessmentScore(qr, qMatch, undefined)
            : { score: sumQuestionnaireResponseItemValues(qr), source: 'sum' as const };
          const current = qrByQuestionnaire.get(qKey);
          const qrDate = qr.authored ?? '';
          if (!current || qrDate > current.authored) {
            qrByQuestionnaire.set(qKey, { authored: qrDate, score: resolved.score });
          }
        } catch { /* missing QR — skip */ }
      }

      // All tasks eligible for the schedule panel (requested, deferred once, completed once-type, and cancelled/stopped)
      const scheduleableTasks = [...requestedTasks, ...onHoldTasks, ...completedTasks, ...cancelledTasks].filter((t) => {
        if (!t.focus?.reference?.startsWith('Questionnaire/')) return false;
        if (isFormRequestTask(t) || isTakeNowAssessmentTask(t)) return false;
        // Must be a scheduled assessment task (not a generic task)
        if (!isScheduledAssessmentTask(t)) return false;
        // Occurrence tasks (have partOf → another Task) belong in the Assessment Log, not here
        if (isAssessmentOccurrenceTask(t)) return false;
        // Questionnaire category double-check (catches legacy unlabelled tasks)
        const qRef = t.focus.reference;
        const match = scoredMeasureTemplates.find((q) => `Questionnaire/${q.id}` === qRef);
        if (match && !isMeasuresQuestionnaire(match)) return false;
        return true;
      });

      // Deduplicate by task ID (requested + completed may overlap for cancelled tasks)
      const seenIds = new Set<string>();
      const apiAssignmentRows: AssignmentRow[] = [];
      for (const t of scheduleableTasks) {
        if (!t.id || seenIds.has(t.id)) continue;
        seenIds.add(t.id);

        const qRef = t.focus!.reference!;
        let qTitle = t.description ?? '';
        const match = scoredMeasureTemplates.find((q) => `Questionnaire/${q.id}` === qRef);
        if (match) qTitle = match.title ?? match.name ?? qTitle;
        const typeName = (qTitle.split('(')[0] ?? qTitle).trim();
        const maxScore = match
          ? resolveMaxScoreForQuestionnaire(match)
          : resolveMaxScore(typeName);
        const frequency = getAssessmentFrequency(t) ?? 'Once';
        const endDate = getAssessmentRecurrenceEnd(t);
        const lastQr = qrByQuestionnaire.get(qRef);

        // Panel status: active schedule vs one-time completed vs stopped (cancelled)
        let panelStatus: string;
        if (t.status === 'cancelled') panelStatus = 'Stopped';
        else if (t.status === 'completed') panelStatus = 'Completed';
        else panelStatus = 'In Progress';

        apiAssignmentRows.push({
          id: `task-${t.id}`,
          type: typeName,
          frequency,
          endDate,
          lastSubmission: lastQr?.authored ?? '--',
          score: lastQr?.score ?? 0,
          maxScore,
          status: panelStatus,
        });
      }

      // In live mode, replace rows completely so modifications are reflected immediately.
      // In mock mode, leave DUMMY_ASSIGNMENTS untouched.
      if (false) {
        setAssignmentRows((prev) => {
          const existingIds = new Set(prev.map((r) => r.id));
          const newRows = apiAssignmentRows.filter((r) => !existingIds.has(r.id));
          return newRows.length > 0 ? [...prev, ...newRows] : prev;
        });
      } else {
        setAssignmentRows(apiAssignmentRows);
      }
    } catch (err) {
      console.error('Failed to fetch task assessments:', err);
      setMedplumErrorOrEmpty(true);
    }
  }, [medplum, resolveMaxScore, patientId, scoredMeasureTemplates]);

  useEffect(() => {
    fetchTaskAssessments();
  }, [fetchTaskAssessments]);

  useEffect(() => {
    fetchScoredMeasureTemplates();
  }, [fetchScoredMeasureTemplates]);

  /** Navigate to the assessment response page (Task output = QuestionnaireResponse). */
  const openAssessmentResponse = (assessment: Assessment) => {
    const access = getAssessmentAccess(assessment);
    const routeId = assessment.assessmentTaskId
      ? assessment.assessmentTaskId
      : assessment.id.startsWith('qr-')
        ? assessment.id
        : assessment.id.startsWith('task-')
          ? assessment.id.slice(5)
          : assessment.id;
    if (clientId) {
      navigate(`/dashboard/clients/${clientId}/measures/assessment/${routeId}`, {
        state: { mode: 'view', assessmentOnly: access === 'assessment-only' },
      });
    } else {
      openEditAssessment(assessment);
    }
  };

  const openEditAssessment = (assessment: Assessment) => {
    setEditingAssessmentId(assessment.id);
    setAssessmentForm(assessment);
    setAssessmentModalOpen(true);
  };

  const handleSaveAssessment = () => {
    const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (editingAssessmentId) {
      setTaskAssessmentsFromApi((prev) =>
        prev.map((item) => (item.id === editingAssessmentId ? { ...assessmentForm } : item)),
      );
    } else {
      const newAssessment: Assessment = {
        ...assessmentForm,
        id: makeId(),
      };
      setTaskAssessmentsFromApi((prev) => [newAssessment, ...prev]);
    }
    setAssessmentModalOpen(false);
  };

  const openModifyAssignmentModal = (row: AssignmentRow) => {
    setModifyingAssignment(row);
    setModifyAssignmentForm({
      frequency: row.frequency,
      endDate: row.endDate ?? '',
      notes: '',
    });
    setModifyModalOpen(true);
  };

  const handleCloseModifyModal = () => {
    setModifyModalOpen(false);
    setModifyingAssignment(null);
  };

  const handleSaveModifyAssignment = async () => {
    if (!modifyingAssignment) return;
    const taskId = modifyingAssignment.id.startsWith('task-')
      ? modifyingAssignment.id.slice(5)
      : modifyingAssignment.id;

    setModifyLoading(true);
    try {
      if (!taskId.startsWith('local-') && !taskId.startsWith('dummy-')) {
        const existing = await medplum.readResource('Task', taskId);

        const freq = modifyAssignmentForm.frequency as AssessmentFrequency;
        clearAssessmentSchedule(existing);
        if (isRecurringFrequency(freq)) {
          const nextSend = calculateNextAssessmentSendDate(
            dateInputValueToDateOnly(new Date()),
            freq,
            modifyAssignmentForm.endDate || undefined,
          );
          if (nextSend) {
            setAssessmentSchedule(
              existing,
              freq as RecurringAssessmentFrequency,
              nextSend,
              modifyAssignmentForm.endDate || undefined,
            );
          }
        }

        await executeTaskMutation(medplum, {
          action: 'update-payload',
          task: existing,
        });
      }

      // Optimistic local update so UI reflects change immediately.
      setAssignmentRows((prev) =>
        prev.map((row) =>
          row.id === modifyingAssignment.id
            ? { ...row, frequency: modifyAssignmentForm.frequency, endDate: modifyAssignmentForm.endDate || undefined }
            : row,
        ),
      );

      // Then sync from backend (replaces all rows with fresh data).
      await fetchTaskAssessments();
      onRefetch?.();
      notifications.show({ title: 'Schedule updated', message: `Schedule updated for ${modifyingAssignment.type}.`, color: 'green' });
      handleCloseModifyModal();
    } catch (err) {
      notifications.show({
        title: 'Update failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
      // Keep modal open on failure so user can retry.
    } finally {
      setModifyLoading(false);
    }
  };

  /** Set Task to cancelled via lifecycle bot; cascades Encounter/ChargeItem for open occurrences. */
  const handleStopSchedule = async (row: AssignmentRow) => {
    const taskId = row.id.startsWith('task-') ? row.id.slice(5) : row.id;

    setStopScheduleLoading(true);
    try {
      if (!taskId.startsWith('local-') && !taskId.startsWith('dummy-')) {
        await executeStopSchedule(medplum, {
          taskId,
          reasonCode: 'clinician-cancelled',
          reasonText: 'Schedule stopped',
        });
      }
      setAssignmentRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: 'Stopped' } : r)),
      );
      onRefetch?.();
      await fetchTaskAssessments();
      notifications.show({
        title: 'Schedule stopped',
        message: `No more ${row.type} sends will be scheduled. Completed assessments remain in the log.`,
        color: 'blue',
      });
    } catch (err) {
      notifications.show({
        title: 'Failed to stop schedule',
        message: formatMedplumClientError(err),
        color: 'red',
      });
    } finally {
      setStopScheduleLoading(false);
      setStopScheduleConfirmRow(null);
    }
  };

  const handleConfirmStopSchedule = () => {
    if (stopScheduleConfirmRow) void handleStopSchedule(stopScheduleConfirmRow);
  };

  const handleCancelAssessment = async (openReplacement = false) => {
    const assessment = cancelAssessmentConfirm;
    if (!assessment) return;
    const taskId =
      assessment.assessmentTaskId ??
      (assessment.id.startsWith('task-') ? assessment.id.slice(5) : undefined);
    if (!taskId || taskId.startsWith('local-') || taskId.startsWith('dummy-')) {
      setCancelAssessmentConfirm(null);
      return;
    }

    const previousRows = taskAssessmentsFromApi;
    setCancelAssessmentLoading(true);
    try {
      await executeCancelAssessment(medplum, {
        taskId,
        reasonCode: 'assigned-in-error',
        reasonText: cancelAssessmentReason.trim() || 'Assigned in error',
      });
      setTaskAssessmentsFromApi((prev) =>
        prev.map((row) =>
          row.id === assessment.id
            ? {
                ...row,
                status: 'Cancelled',
                score: 0,
                cancellationReason: cancelAssessmentReason.trim() || 'Assigned in error',
                cancelledAt: new Date().toISOString(),
              }
            : row,
        ),
      );
      onRefetch?.();
      await fetchTaskAssessments();
      notifications.show({
        title: 'Assessment cancelled',
        message: `${assessment.assessmentType} will no longer be available for completion. It remains in the assessment history.`,
        color: 'blue',
      });
      setCancelAssessmentConfirm(null);
      setCancelAssessmentReason('Assigned in error');
      if (openReplacement) {
        openAddAssignmentModal();
      }
    } catch (err) {
      setTaskAssessmentsFromApi(previousRows);
      notifications.show({
        title: 'Failed to cancel assessment',
        message: formatMedplumClientError(err),
        color: 'red',
      });
    } finally {
      setCancelAssessmentLoading(false);
    }
  };

  /** Resume a stopped (cancelled) schedule by setting Task back to requested. */
  const handleActivateSchedule = async (row: AssignmentRow) => {
    const taskId = row.id.startsWith('task-') ? row.id.slice(5) : row.id;
    setActivateScheduleRowId(row.id);
    try {
      if (!taskId.startsWith('local-') && !taskId.startsWith('dummy-')) {
        await executeActivateSchedule(medplum, { taskId });
      }
      setAssignmentRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: 'In Progress' } : r)),
      );
      onRefetch?.();
      await fetchTaskAssessments();
      notifications.show({
        title: 'Schedule activated',
        message: `${row.type} is active again. Future sends can be scheduled per your recurrence settings.`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Failed to activate schedule',
        message: formatMedplumClientError(err),
        color: 'red',
      });
    } finally {
      setActivateScheduleRowId(null);
    }
  };

  /** Opens the Add Assignment modal (recurring or single with Once). Used by "+ Schedule Assessment" in Schedule Assessments section. */
  const openAddAssignmentModal = () => {
    setAssignmentForm({
      selectedQuestionnaireId: '',
      frequency: 'Once',
      notes: '',
      startDate: '',
      endDate: '',
      dueDate: '',
      episodeId: '',
    });
    fetchScoredMeasureTemplates();
    setAssignModalOpen(true);
  };

  /** Opens the Add Assessment modal (single assessment / take or assign). Used by "Assessment" in Assessments Log. */
  const openAddAssessmentModalForLog = () => {
    setAddAssessmentForm({ selectedQuestionnaireId: '', dueDate: '' });
    fetchScoredMeasureTemplates();
    setAddAssessmentModalOpen(true);
  };

  /** Add a new row to the Schedule Assessments table from the form state. */
  const addAssignmentRowFromForm = (assessmentType: string, taskId?: string) => {
    const nowIso = new Date().toISOString();
    const typeName = (assessmentType.split('(')[0] ?? assessmentType).trim();
    const maxScore = resolveMaxScore(typeName);
    const dueDate = assignmentForm.dueDate?.trim() || undefined;
    const id = taskId ? `task-${taskId}` : `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setAssignmentRows((prev) => [
      {
        id,
        type: typeName,
        frequency: assignmentForm.frequency,
        lastSubmission: '--',
        score: 0,
        maxScore,
        status: 'In Progress',
        assessment: {
          id,
          assessmentType: typeName,
          date: dueDate ?? nowIso.slice(0, 10),
          time: '',
          provider: '',
          score: 0,
          maxScore,
          administeredBy: '',
          status: 'In Progress',
          requestedDate: nowIso,
          dueDate,
        },
      },
      ...prev,
    ]);
  };

  const handleAssignAssessment = async () => {
    const selectedQ = scoredMeasureTemplates.find(
      (q) => q.id === assignmentForm.selectedQuestionnaireId,
    );
    if (!selectedQ?.id) {
      if (false) {
        handleAssignAssessmentDummy();
      } else {
        notifications.show({
          title: 'Assessment required',
          message: 'Select an active Measures questionnaire template.',
          color: 'red',
        });
      }
      return;
    }

    if (accessibleEpisodes.length === 0) {
      notifications.show({
        title: 'Episode of Care required',
        message: 'No accessible Episodes of Care. Create or request access to an episode before scheduling.',
        color: 'red',
      });
      return;
    }
    if (!assignmentForm.episodeId?.trim()) {
      notifications.show({
        title: 'Episode of Care required',
        message: 'Select an Episode of Care for this assessment.',
        color: 'red',
      });
      return;
    }

    const freq = assignmentForm.frequency as AssessmentFrequency;
    const isOnce = assignmentForm.frequency === 'Once';

    // ── Validate send / start date ────────────────────────────────────────
    if (assignmentForm.startDate) {
      const startD = new Date(assignmentForm.startDate);
      if (!Number.isNaN(startD.getTime()) && startD.getTime() < Date.now()) {
        notifications.show({
          title: 'Invalid send time',
          message: 'Scheduled send time must be in the future.',
          color: 'red',
        });
        return;
      }
    }

    setAssignmentLoading(true);
    const assessmentType = selectedQ.title ?? selectedQ.name ?? 'Assessment';
    const dueDate = assignmentForm.dueDate?.trim() || undefined;

    // For recurring: startDate drives next-send. Null means "now" (immediate first send).
    // For Once: startDate is the deferred send instant (blank = send now).
    const startDateStr = assignmentForm.startDate
      ? dateInputValueToDateOnly(new Date(assignmentForm.startDate))
      : dateInputValueToDateOnly(new Date());
    const onceDeferredSendIso =
      isOnce && assignmentForm.startDate
        ? assignmentForm.startDate
        : undefined;
    const nextSend = isRecurringFrequency(freq)
      ? assignmentForm.startDate
        ? startDateStr
        : calculateNextAssessmentSendDate(startDateStr, freq, assignmentForm.endDate || undefined)
      : onceDeferredSendIso;

    const standingOrderBody = buildAssessmentAssignmentTask({
      patientId,
      questionnaireId: selectedQ.id,
      questionnaireDisplay: assessmentType,
      practitioner,
      dueDate: isOnce ? dueDate : undefined,
      episodeId: assignmentForm.episodeId,
      accounts: patientAccountRefs,
      frequency: freq,
      recurrenceEndDate: assignmentForm.endDate || undefined,
      // For future start dates, nextSend is the earliest-send gate for the recurring bot / Once cron.
      // For null/now, nextSend is the next recurrence after the first immediate occurrence.
      nextSendDate: nextSend ?? undefined,
    });

    try {
      const createBody =
        onceDeferredSendIso
          ? { ...standingOrderBody, status: 'on-hold' as const }
          : standingOrderBody;
      const createdTask = isRecurringFrequency(freq)
        ? await executePortalTaskLifecycle(medplum, {
            action: 'create-assessment-standing',
            patientId,
            questionnaireId: selectedQ.id,
            description: assessmentType,
            episodeId: assignmentForm.episodeId,
            extensions: standingOrderBody.extension,
            taskInput: standingOrderBody.input,
            restriction: standingOrderBody.restriction,
          })
        : await executePortalTaskLifecycle(medplum, {
            action: 'create-assessment-once',
            patientId,
            questionnaireId: selectedQ.id,
            assignmentId: `${patientId}:${selectedQ.id}:${Date.now()}`,
            description: assessmentType,
            due: isOnce ? dueDate : undefined,
            episodeId: assignmentForm.episodeId,
            extensions: createBody.extension,
            taskInput: createBody.input,
            restriction: createBody.restriction,
            status: createBody.status,
          });

      // For recurring schedules with an immediate or no start date, create the first
      // occurrence Task now so the client can see it immediately in their portal.
      // For future start dates, the Recurring Assessment Bot creates the occurrence when due.
      if (isRecurringFrequency(freq) && !assignmentForm.startDate) {
        try {
          const occurrenceBody = buildAssessmentOccurrenceTask(createdTask, dueDate);
          await executePortalTaskLifecycle(medplum, {
            action: 'create-assessment-occurrence',
            patientId,
            questionnaireId: selectedQ.id,
            standingOrderTaskId: createdTask.id,
            scheduledOccurrenceDate: dateInputValueToDateOnly(new Date()),
            description: occurrenceBody.description,
            due: dueDate,
            episodeId: assignmentForm.episodeId,
            extensions: occurrenceBody.extension,
          });
        } catch (occErr) {
          // Non-fatal: the standing order was created; the bot will create the occurrence.
          console.warn('Failed to create immediate occurrence Task — recurring bot will handle:', occErr);
        }
      }

      await fetchTaskAssessments();
      setMedplumErrorOrEmpty(false);
      onRefetch?.();
      notifications.show({ title: 'Assessment scheduled', message: 'Assessment scheduled for client.', color: 'green' });
    } catch (error) {
      console.error('Failed to create assessment task:', error);
      notifications.show({ title: 'Assignment failed', message: formatMedplumClientError(error), color: 'red' });
    } finally {
      setAssignmentLoading(false);
      setAssignModalOpen(false);
    }
  };

  /** Mock-catalog fallback when no server Questionnaire is selected. */
  const handleAssignAssessmentDummy = () => {
    const mockOption = MOCK_ASSESSMENT_TYPE_OPTIONS.find(
      (o) => o.value === assignmentForm.selectedQuestionnaireId,
    );
    if (!mockOption) {
      notifications.show({ title: 'Assessment type required', message: 'Select an assessment type to schedule.', color: 'red' });
      setAssignModalOpen(false);
      return;
    }
    addAssignmentRowFromForm(mockOption.label);
    notifications.show({ title: 'Assessment scheduled', message: `${mockOption.label.split('(')[0].trim()} added to schedule.`, color: 'green' });
    setAssignModalOpen(false);
  };

  /** Create a Task for the given questionnaire (used by Add Assessment modal: Take Now and Assign). */
  const createTaskForQuestionnaire = useCallback(
    async (questionnaireId: string, dueDate?: string, episodeId?: string): Promise<Task | null> => {
      const selectedQ = scoredMeasureTemplates.find((q) => q.id === questionnaireId);
      if (!selectedQ?.id) return null;
      const assessmentType = selectedQ.title ?? selectedQ.name ?? 'Assessment';

      const task = buildProviderTakeNowTask({
        patientId,
        questionnaireId: selectedQ.id,
        questionnaireDisplay: assessmentType,
        practitioner,
        dueDate,
        episodeId,
        accounts: patientAccountRefs,
      });

      return executeTaskMutation(medplum, { action: 'create-payload', task }) as Promise<Task>;
    },
    [medplum, scoredMeasureTemplates, patientId, practitioner, patientAccountRefs],
  );

  const handleTakeNowAssessment = async () => {
    const id = addAssessmentForm.selectedQuestionnaireId.trim();
    if (!id) {
      notifications.show({ title: 'Assessment type required', message: 'Select an assessment type to take.', color: 'red' });
      return;
    }

    if (!false && !addAssessmentEpisodeId) {
      notifications.show({ title: 'Episode of Care required', message: 'Select an Episode of Care before starting an assessment.', color: 'red' });
      return;
    }

    setAddAssessmentLoading(true);

    const isMockId =
      false &&
      (id in MOCK_TYPE_TO_TEMPLATE_ID || MOCK_ASSESSMENT_TYPE_OPTIONS.some((o) => o.value === id));
    if (isMockId) {
      const templateId = MOCK_TYPE_TO_TEMPLATE_ID[id] ?? 'mock-measure-1';
      const assessmentType = MOCK_ASSESSMENT_TYPE_OPTIONS.find((o) => o.value === id)?.label?.split('(')[0]?.trim() || id;
      setAddAssessmentModalOpen(false);
      setAddAssessmentLoading(false);
      navigate(
        clientId
          ? `/dashboard/clients/${clientId}/measures/assessment/mock-${templateId}`
          : `/client-portal/assessments/mock-${templateId}`,
        {
          state: {
            mode: 'take-now',
            returnTo: clientId ? `/dashboard/clients/${clientId}?tab=measures` : undefined,
            assessmentMeta: { assessmentType, maxScore: resolveMaxScore(assessmentType) },
          },
        },
      );
      return;
    }

    try {
      const task = await createTaskForQuestionnaire(id, addAssessmentForm.dueDate || undefined, addAssessmentEpisodeId || undefined);
      if (task?.id) {
        setAddAssessmentModalOpen(false);
        navigate(
          clientId
            ? `/dashboard/clients/${clientId}/measures/assessment/${task.id}`
            : `/client-portal/assessments/${task.id}`,
          {
            state: {
              mode: 'take-now',
              returnTo: clientId ? `/dashboard/clients/${clientId}?tab=measures` : undefined,
            },
          },
        );
      } else {
        notifications.show({ title: 'Error', message: 'Could not start assessment. Questionnaire template not found or inactive.', color: 'red' });
      }
    } catch (error) {
      console.error('Failed to create task for take now:', error);
      notifications.show({ title: 'Error', message: formatMedplumClientError(error), color: 'red' });
    } finally {
      setAddAssessmentLoading(false);
    }
  };

  // TODO: wire up when assessment assignment from log view is ready
  // const handleAssignAssessmentFromLog = async () => { ... };

  /** Chart bundle (QuestionnaireResponse–derived) plus Task rows; in mock mode chart is unused. */
  const mergedChartAndTasks = useMemo(() => {
    if (false) return taskAssessmentsFromApi;
    const dedupeById = (rows: Assessment[]): Assessment[] => {
      const seen = new Set<string>();
      return rows.filter((a) => {
        if (seen.has(a.id)) return false;
        seen.add(a.id);
        return true;
      });
    };
    if (chartAssessments.length > 0) {
      const chartIds = new Set(chartAssessments.map((a) => a.id));
      const chartTaskIds = new Set(
        chartAssessments.map((a) => a.assessmentTaskId).filter((id): id is string => !!id),
      );
      const extras = taskAssessmentsFromApi.filter(
        (a) =>
          (a.status === 'In Progress' || a.status === 'Cancelled') &&
          a.id.startsWith('task-') &&
          !chartIds.has(a.id) &&
          !chartTaskIds.has(a.id.replace(/^task-/, '')),
      );
      return dedupeById([...chartAssessments, ...extras]);
    }
    return dedupeById(taskAssessmentsFromApi);
  }, [chartAssessments, taskAssessmentsFromApi]);

  // Filter and sort assessments for the log table
  const filteredAssessments = useMemo(() => {
    const filtered =
      assessmentTypeFilter.length === 0
        ? []
        : mergedChartAndTasks.filter((a) => assessmentTypeFilter.includes(a.assessmentType));

    return [...filtered].sort((a, b) => {
      const aDate = assessmentDateSortValue(a.date);
      const bDate = assessmentDateSortValue(b.date);
      return assessmentSortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    });
  }, [mergedChartAndTasks, assessmentSortDirection, assessmentTypeFilter]);

  /** Dummy log only in mock mode when no chart/tasks/completions exist. */
  const useDummyData =
    false &&
    mergedChartAndTasks.length === 0 &&
    localCompletedAssessments.length === 0;

  useEffect(() => {
    const state = location.state as { completedAssessment?: Assessment } | null;
    if (state?.completedAssessment) {
      setLocalCompletedAssessments((prev) => {
        if (prev.some((a) => a.id === state.completedAssessment!.id)) return prev;
        return [state.completedAssessment!, ...prev];
      });
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  /** Schedule Assessments table state; demo rows only in mock mode (real mode starts empty / user-created). */
  const [assignmentRows, setAssignmentRows] = useState<AssignmentRow[]>(() =>
    false ? [...DUMMY_ASSIGNMENTS] : [],
  );
  const logList = useMemo(() => {
    const base = useDummyData ? DUMMY_LOG_ASSESSMENTS : filteredAssessments;
    return localCompletedAssessments.length > 0 ? [...localCompletedAssessments, ...base] : base;
  }, [useDummyData, filteredAssessments, localCompletedAssessments]);

  const logListFiltered = useMemo(() => {
    const [start, end] = logDateRange;
    let list = logList;
    if (start || end) {
      const startKey = start ? formatDateOnlyLocal(start) : null;
      const endKey = end ? formatDateOnlyLocal(end) : null;
      list = logList.filter((a) => {
        const key = toIsoDateOnly(a.date);
        if (!key) return false;
        if (startKey && key < startKey) return false;
        if (endKey && key > endKey) return false;
        return true;
      });
    }
    return [...list].sort((a, b) => {
      const aDate = assessmentDateSortValue(a.date);
      const bDate = assessmentDateSortValue(b.date);
      return assessmentSortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    });
  }, [logList, logDateRange, assessmentSortDirection]);

  /** Completed assessments from the log — source of truth for trends chart and filter. */
  const completedLogAssessments = useMemo(() => {
    const base = useDummyData ? DUMMY_LOG_ASSESSMENTS : mergedChartAndTasks;
    const list =
      localCompletedAssessments.length > 0 ? [...localCompletedAssessments, ...base] : base;
    return list.filter((a) => a.status === 'Completed' && a.date?.trim());
  }, [useDummyData, mergedChartAndTasks, localCompletedAssessments]);

  const assessmentTypesInLog = useMemo(() => {
    const types = new Set<string>();
    for (const a of completedLogAssessments) types.add(a.assessmentType);
    return [...types].sort((a, b) => a.localeCompare(b));
  }, [completedLogAssessments]);

  const assessmentChartOptions = useMemo(
    () => assessmentTypesInLog.map((t) => ({ value: t, label: t })),
    [assessmentTypesInLog],
  );

  useEffect(() => {
    const allowed = new Set(assessmentTypesInLog);
    setSelectedAssessment((prev) => prev.filter((v) => allowed.has(v)));
  }, [assessmentTypesInLog]);

  const trendDataPoints = useMemo(
    () => buildAssessmentTrendDataPoints(completedLogAssessments),
    [completedLogAssessments],
  );

  const normalizedTrends = useMemo(() => {
    const maxByType = maxScoreByAssessmentType(completedLogAssessments);
    return normalizeAssessmentTrendDataPoints(trendDataPoints, maxByType);
  }, [completedLogAssessments, trendDataPoints]);

  const trendMaxByType = useMemo(
    () => maxScoreByAssessmentType(completedLogAssessments),
    [completedLogAssessments],
  );

  const allChartSeries = useMemo(
    () =>
      assessmentTypesInLog.map((name) => ({
        name,
        color: stablePaletteColorForKey(name),
      })),
    [assessmentTypesInLog],
  );

  const seriesWithTrendData = useMemo(() => {
    const typesWithData = new Set<string>();
    for (const point of trendDataPoints) {
      for (const key of Object.keys(point)) {
        if (key === 'date' || key === 'session') continue;
        const v = (point as unknown as Record<string, unknown>)[key];
        if (typeof v === 'number') typesWithData.add(key);
      }
    }
    return allChartSeries.filter((s) => typesWithData.has(s.name));
  }, [trendDataPoints, allChartSeries]);

  const chartSeries =
    selectedAssessment.length === 0
      ? seriesWithTrendData
      : seriesWithTrendData.filter((s) => selectedAssessment.includes(s.name));

  const assessmentLogTypeOptions = useMemo(() => {
    const types = new Set(logList.map((a) => a.assessmentType).filter(Boolean));
    const defaults = ['PHQ-9', 'GAD-7', 'PCL-5', 'WAI'];
    const all = [...new Set([...defaults, ...types])];
    return all.map((t) => ({ value: t, label: t }));
  }, [logList]);

  const assessmentLogColumns = useMemo(
    (): UnifyTableColumn<Assessment>[] => [
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        getValue: (row) => row.date,
        render: (row) => <Text size="sm">{formatDate(row.date)}</Text>,
      },
      {
        key: 'type',
        label: 'Type',
        sortable: true,
        getValue: (row) => row.assessmentType,
        render: (row) => (
          <Badge variant="light" size="sm" color={getAssessmentTypeBadgeColor(row.assessmentType)}>
            <CodeableConceptDisplay value={getAssessmentCodeableConcept(row)} />
          </Badge>
        ),
      },
      {
        key: 'score',
        label: 'Score',
        sortable: true,
        getValue: (row) => String(row.score),
        render: (row) => {
          const isCancelled = row.status === 'Cancelled';
          const maxScore = row.maxScore ?? 100;
          const template = findTemplateByAssessmentType(scoredMeasureTemplates, row.assessmentType);
          if (isCancelled) return <Text size="sm" c="gray.5">--</Text>;
          return (
            <Badge variant="light" size="sm" color={getScoreBadgeColor(row.score, maxScore, template)}>
              {row.score}/{maxScore}
            </Badge>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        getValue: (row) => row.status ?? 'Completed',
        render: (row) => {
          const isCancelled = row.status === 'Cancelled';
          return (
            <Badge
              variant="light"
              size="sm"
              color={isCancelled ? 'gray' : row.status === 'Completed' ? 'green' : 'yellow'}
            >
              {row.status ?? 'Completed'}
            </Badge>
          );
        },
      },
      {
        key: 'administeredBy',
        label: 'Assigned by',
        sortable: true,
        getValue: (row) => row.administeredBy || '--',
        render: (row) => (
          <Text size="sm" c="gray.7">
            {row.administeredBy || '--'}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        width: 48,
        render: (row) => {
          const isPending = row.status === 'In Progress';
          if (!isPending || !row.id.startsWith('task-')) return null;
          return (
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label="Assessment options"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconBan size={14} />}
                  color="red"
                  onClick={() => {
                    setCancelAssessmentReason('Assigned in error');
                    setCancelAssessmentConfirm(row);
                  }}
                >
                  Cancel assessment
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          );
        },
      },
    ],
    [scoredMeasureTemplates],
  );

  return (
    <>
      <Stack gap="lg">
        {/* A) Full-width Assessment Trends at top */}
        <ChartCard
          title="Assessment Trends"
          headerRight={
            <Group gap="md" align="center" wrap="wrap">
              {trendDataPoints.length > 0 && chartSeries.length > 0 && chartSeries.map((s) => (
                <Group key={s.name} gap={6} align="center">
                  <Box w={12} h={12} style={{ borderRadius: 2, backgroundColor: s.color }} />
                  <Text size="sm" c="gray.7">{s.name}</Text>
                </Group>
              ))}
              {assessmentChartOptions.length > 0 ? (
                <FilterMultiSelect
                  allLabel="All Assessments"
                  data={assessmentChartOptions}
                  value={selectedAssessment}
                  onChange={setSelectedAssessment}
                  w={220}
                />
              ) : null}
            </Group>
          }
        >
          {trendDataPoints.length > 0 && chartSeries.length > 0 ? (
            <UnifyLineChart
              h={380}
              data={normalizedTrends}
              dataKey="date"
              series={chartSeries}
              curveType="monotone"
              withDots
              gridAxis="xy"
              connectNulls
              dotProps={{ r: 5, strokeWidth: 0 }}
              withLegend={false}
              yAxisProps={{ domain: [0, 100], tickFormatter: (v: number) => v === 100 ? 'Max/100%' : v === 0 ? 'Min/0%' : `${v}%` }}
              tooltipProps={{
                trigger: 'hover',
                content: (contentProps: unknown) => {
                  const props = contentProps as {
                    label?: string | number;
                    payload?: readonly { name?: string; value?: number; dataKey?: string; color?: string }[];
                    labelFormatter?: (label: unknown, payload: unknown[]) => React.ReactNode;
                  };
                  const { label, payload, labelFormatter } = props;
                  const payloadCopy = payload ? [...payload] : undefined;
                  const displayLabel =
                    labelFormatter && payloadCopy ? labelFormatter(label, payloadCopy) : label;
                  const enriched = enrichAssessmentTrendTooltipPayload(
                    label,
                    payloadCopy as unknown as Record<string, unknown>[] | undefined,
                    trendDataPoints,
                    trendMaxByType,
                  );
                  return (
                    <UnifyChartTooltip
                      label={displayLabel}
                      payload={enriched as Record<string, unknown>[] | undefined}
                      series={chartSeries}
                    />
                  );
                },
              }}
              xAxisProps={{
                padding: { left: 0, right: 70 },
              }}
            />
          ) : trendDataPoints.length > 0 && selectedAssessment.length > 0 && chartSeries.length === 0 ? (
            <Text c="gray.6" ta="center" py="xl">
              No series selected
            </Text>
          ) : (
            <Text c="gray.6" ta="center" py="xl">
              No trend data available
            </Text>
          )}
        </ChartCard>

        {/* A2) Half-width panels: Assignments | Health Metrics */}
        <Grid align="stretch">
          <Grid.Col span={{ base: 12, md: FEATURE_VISIBILITY.chartHealthMetrics ? 6 : 12 }} style={{ display: 'flex' }}>
            <Card
              withBorder
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 400,
                maxHeight: 400,
              }}
            >
              <Group justify="space-between" mb="md">
                <Title order={4}>Schedule Assessments</Title>
                <Button size="sm" leftSection={<IconPlus size={14} />} onClick={openAddAssignmentModal}>
                  Schedule
                </Button>
              </Group >
              <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table
                  styles={{
                    th: { paddingTop: '1rem', paddingBottom: '1rem' },
                    td: { paddingTop: '1rem', paddingBottom: '1rem' },
                  }}
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Frequency</Table.Th>
                      <Table.Th>Date of last submission</Table.Th>
                      <Table.Th>Score</Table.Th>
                      <Table.Th w={48} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {assignmentRows.length === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={5}>
                          <Text size="sm" c="gray.5" ta="center" py="md">
                            No scheduled assessments. Use &ldquo;+ Schedule&rdquo; to assign a recurring measure.
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    {assignmentRows.map((row) => {
                      const isStopped = row.status === 'Stopped';
                      const isActiveSchedule = row.status === 'In Progress';
                      return (
                        <Table.Tr key={row.id}>
                          <Table.Td>
                            <Badge variant="light" size="md" color={getAssessmentTypeBadgeColor(row.type)}>
                              {row.type}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{row.frequency}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{formatDate(row.lastSubmission)}</Text>
                          </Table.Td>
                          <Table.Td>
                            {row.lastSubmission && row.lastSubmission !== '--' && !Number.isNaN(new Date(row.lastSubmission).getTime()) ? (
                              <Badge
                                variant="light"
                                size="md"
                                color={getScoreBadgeColor(
                                  row.score,
                                  row.maxScore,
                                  findTemplateByAssessmentType(scoredMeasureTemplates, row.type),
                                )}
                              >
                                {row.score}/{row.maxScore}
                              </Badge>
                            ) : (
                              <Text size="sm" c="gray.5">--</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Menu shadow="md" width={180} position="bottom-end" trigger="hover" openDelay={100} closeDelay={300} offset={0}>
                              <Menu.Target>
                                <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Assignment options">
                                  <IconDots size={16} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => openModifyAssignmentModal(row)}>
                                  Modify
                                </Menu.Item>
                                {isActiveSchedule && (
                                  <Menu.Item leftSection={<IconBan size={14} />} color="red" onClick={() => setStopScheduleConfirmRow(row)}>
                                    Stop schedule
                                  </Menu.Item>
                                )}
                                {isStopped && (
                                  <Menu.Item
                                    leftSection={<IconPlayerPlay size={14} />}
                                    color="green"
                                    disabled={activateScheduleRowId === row.id}
                                    onClick={() => void handleActivateSchedule(row)}
                                  >
                                    Activate
                                  </Menu.Item>
                                )}
                              </Menu.Dropdown>
                            </Menu>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Box>
            </Card >
          </Grid.Col >

          {FEATURE_VISIBILITY.chartHealthMetrics ? (
          <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex' }}>
            <Card
              withBorder
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 400,
                maxHeight: 400,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Group gap="sm" mb="md" wrap="wrap">
                <Title order={4} mb={0}>Health Metrics</Title>
                <LocalhostOnlyBadge />
              </Group>
              <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {healthMetrics.length === 0 ? (
                  <Text c="gray.6" ta="center" py="xl">
                    No health metrics available
                  </Text>
                ) : (
                  <>
                    <UnifyLineChart
                      h={200}
                      data={healthMetrics}
                      dataKey="date"
                      series={[
                        { name: 'sleep', color: 'indigo.6' },
                      ]}
                      curveType="linear"
                      withDots
                      withLegend
                      gridAxis="xy"
                      connectNulls
                      dotProps={{ r: 4, strokeWidth: 2 }}
                      activeDotProps={{ r: 0 }}
                      xAxisProps={{
                        padding: { left: 0, right: 70 },
                      }}
                    />
                    <Table mt="md">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Date</Table.Th>
                          <Table.Th>Steps</Table.Th>
                          <Table.Th>Sleep (hrs)</Table.Th>
                          <Table.Th>Screen Time</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {healthMetrics.slice(0, 5).map((metric, index) => (
                          <Table.Tr key={index}>
                            <Table.Td>{metric.date}</Table.Td>
                            <Table.Td>{metric.steps.toLocaleString()}</Table.Td>
                            <Table.Td>{metric.sleep}</Table.Td>
                            <Table.Td>{metric.screenTime}h</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </>
                )}
              </Box>
            </Card>
          </Grid.Col>
          ) : null}
        </Grid >

        {/* B) Full-width Assessments Log */}
        < Box ref={assessmentsLogSectionRef} >
          <Card withBorder>
            <Group justify="space-between" mb="md" wrap="wrap" align="center">
              <Title order={4}>Assessments Log</Title>
              <Group gap="md" wrap="wrap" align="center">
                <DateRangeSelect
                  value={logDateRange}
                  onChange={setLogDateRange}
                  placeholder="Date range"
                  w={240}
                />
                <FilterMultiSelect
                  allLabel="All Types"
                  data={assessmentLogTypeOptions}
                  value={assessmentTypeFilter}
                  onChange={setAssessmentTypeFilter}
                  w={240}
                />
                <Button leftSection={<IconPlus size={14} />} onClick={openAddAssessmentModalForLog}>
                  Take Now
                </Button>
              </Group >
            </Group >
            <UnifyTable<Assessment>
              withPaper={false}
              searchable={false}
              columns={assessmentLogColumns}
              data={logListFiltered}
              getRowKey={(row) => row.id}
              sortable
              pagination
              defaultRowsPerPage={25}
              emptyMessage="No assessments in this range"
              onRowClick={(row) => {
                if (row.status !== 'Cancelled') openAssessmentResponse(row);
              }}
              tableProps={{ highlightOnHover: true }}
            />
          </Card >
        </Box >

      </Stack >

      <Modal
        opened={assessmentModalOpen}
        onClose={() => setAssessmentModalOpen(false)}
        title={editingAssessmentId ? 'Edit Assessment' : 'Add Assessment'}
        centered
        size="lg"
      >
        <Stack gap="md">
          <Group grow>
            <Select
              label="Assessment type"
              data={[
                { value: 'PHQ-9', label: 'PHQ-9 (Depression)' },
                { value: 'GAD-7', label: 'GAD-7 (Anxiety)' },
                { value: 'PCL-5', label: 'PCL-5 (PTSD)' },
                { value: 'WAI', label: 'WAI (Alliance)' },
              ]}
              value={assessmentForm.assessmentType}
              onChange={(value) =>
                setAssessmentForm((prev) => ({ ...prev, assessmentType: value || 'PHQ-9' }))
              }
              required
            />
            <Select
              label="Status"
              data={[
                { value: 'Completed', label: 'Completed' },
                { value: 'In Progress', label: 'In Progress' },
                { value: 'Pending Review', label: 'Pending Review' },
              ]}
              value={assessmentForm.status}
              onChange={(value) =>
                setAssessmentForm((prev) => ({ ...prev, status: (value as Assessment['status']) || 'Completed' }))
              }
            />
          </Group>
          <Group grow>
            <TextInput
              label="Date"
              placeholder="2026-02-15"
              value={assessmentForm.date}
              onChange={(e) => setAssessmentForm((prev) => ({ ...prev, date: e.target.value }))}
              required
            />
            <TextInput
              label="Time"
              placeholder="10:00 AM"
              value={assessmentForm.time}
              onChange={(e) => setAssessmentForm((prev) => ({ ...prev, time: e.target.value }))}
            />
          </Group>
          <Group grow>
            <TextInput
              label="Provider"
              placeholder="Dr. Jane Smith"
              value={assessmentForm.provider}
              onChange={(e) => setAssessmentForm((prev) => ({ ...prev, provider: e.target.value }))}
            />
            <TextInput
              label="Assigned by"
              placeholder="Vida / supervising provider"
              value={assessmentForm.administeredBy}
              onChange={(e) =>
                setAssessmentForm((prev) => ({ ...prev, administeredBy: e.target.value }))
              }
            />
          </Group>
          <Group grow>
            <NumberInput
              label="Score"
              min={0}
              value={assessmentForm.score}
              onChange={(value) => {
                const numericValue = typeof value === 'number' ? value : Number(value || 0);
                setAssessmentForm((prev) => ({ ...prev, score: numericValue }));
              }}
              required
            />
            <NumberInput
              label="Max score"
              min={1}
              value={assessmentForm.maxScore}
              onChange={(value) => {
                const numericValue = typeof value === 'number' ? value : Number(value || 0);
                setAssessmentForm((prev) => ({ ...prev, maxScore: numericValue }));
              }}
            />
            <Select
              label="Severity"
              data={[
                { value: 'Minimal', label: 'Minimal' },
                { value: 'Mild', label: 'Mild' },
                { value: 'Moderate', label: 'Moderate' },
                { value: 'Moderately Severe', label: 'Moderately Severe' },
                { value: 'Severe', label: 'Severe' },
              ]}
              value={assessmentForm.severity}
              onChange={(value) =>
                setAssessmentForm((prev) => ({ ...prev, severity: value || '' }))
              }
            />
          </Group>
          <Textarea
            label="Notes"
            placeholder="Context, symptom changes, or follow-up actions."
            value={assessmentForm.notes}
            onChange={(e) => setAssessmentForm((prev) => ({ ...prev, notes: e.target.value }))}
            minRows={3}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setAssessmentModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAssessment}>
              {editingAssessmentId ? 'Save changes' : 'Add assessment'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title="Schedule Assessment"
        centered
        size="lg"
        closeOnClickOutside
        trapFocus
      >
        <Stack gap="md">
          <Text size="sm" c="gray.6">
            {assignmentForm.frequency === 'Once'
              ? 'Assign a single assessment to this client (once) or choose a recurring schedule.'
              : 'Assign a scored measure to this patient and set the recurring schedule.'}
          </Text>

          {accessibleEpisodes.length > 0 ? (
            <Select
              label="Episode of Care"
              placeholder="Select episode..."
              data={toEpisodeSelectOptions(accessibleEpisodes)}
              value={assignmentForm.episodeId || null}
              onChange={(value) =>
                setAssignmentForm((prev) => ({ ...prev, episodeId: value ?? '' }))
              }
              required
              clearable={false}
            />
          ) : (
            <Text size="sm" c="orange.7">
              No accessible Episodes of Care. Create or request access to an episode before scheduling an assessment.
            </Text>
          )}

          <Select
            label="Assessment type"
            placeholder="Select a scored measure..."
            data={measuresTemplateSelectData}
            value={assignmentForm.selectedQuestionnaireId || null}
            onChange={(value) =>
              setAssignmentForm((prev) => ({ ...prev, selectedQuestionnaireId: value ?? '' }))
            }
            searchable
            required
          />

          <Select
            label="Frequency"
            data={FREQUENCY_SELECT_DATA}
            value={assignmentForm.frequency}
            onChange={(value) =>
              setAssignmentForm((prev) => ({ ...prev, frequency: value ?? prev.frequency }))
            }
          />

          {assignmentForm.frequency === 'Once' ? (
            <>
              <DateTimePicker
                label="Send date & time"
                description="Leave blank to send immediately. Select a future date/time to schedule."
                placeholder="Now (send immediately)"
                value={assignmentForm.startDate ? new Date(assignmentForm.startDate) : null}
                onChange={(v) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    startDate: dateTimePickerValueToIsoStored(v),
                    dueDate: '',
                  }))
                }
                leftSection={<IconCalendarEvent size={16} />}
                minDate={new Date()}
                clearable
              />
              <div>
                <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>Send</Text>
                <Text size="sm">
                  {assignmentForm.startDate
                    ? formatDateTimeFriendly(new Date(assignmentForm.startDate))
                    : 'Now (immediately)'}
                </Text>
              </div>
            </>
          ) : (
            <>
              <DateTimePicker
                label="Start date & time"
                description="Leave blank to send immediately. Select a future date/time to schedule."
                placeholder="Now (send immediately)"
                value={assignmentForm.startDate ? new Date(assignmentForm.startDate) : null}
                onChange={(v) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    startDate: dateTimePickerValueToIsoStored(v),
                  }))
                }
                leftSection={<IconCalendarEvent size={16} />}
                minDate={new Date()}
                clearable
              />
              <div>
                <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>First send</Text>
                <Text size="sm">
                  {assignmentForm.startDate
                    ? formatDateTimeFriendly(new Date(assignmentForm.startDate))
                    : 'Now (immediately)'}
                </Text>
              </div>

              <DatePickerInput
                label="End date (optional)"
                placeholder="No end date"
                value={assignmentForm.endDate ? new Date(assignmentForm.endDate + 'T00:00:00') : null}
                onChange={(v) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    endDate: dateInputValueToDateOnly(v),
                  }))
                }
                leftSection={<IconCalendarEvent size={16} />}
                minDate={new Date()}
                clearable
              />
            </>
          )}

          <Textarea
            label="Instructions / notes for client"
            placeholder="Optional notes"
            value={assignmentForm.notes}
            onChange={(e) =>
              setAssignmentForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            minRows={2}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssignAssessment}
              loading={assignmentLoading}
              disabled={
                !assignmentForm.selectedQuestionnaireId ||
                accessibleEpisodes.length === 0 ||
                !assignmentForm.episodeId?.trim()
              }
            >
              Assign
            </Button>
          </Group>
        </Stack >
      </Modal >

      <Modal
        opened={addAssessmentModalOpen}
        onClose={() => setAddAssessmentModalOpen(false)}
        title="Add Assessment"
        centered
        size="lg"
        closeOnClickOutside
        trapFocus
      >
        <Stack gap="md">
          {accessibleEpisodes.length > 0 ? (
            <Select
              label="Episode of Care"
              placeholder="Select episode..."
              data={toEpisodeSelectOptions(accessibleEpisodes)}
              value={addAssessmentEpisodeId || null}
              onChange={(v) => setAddAssessmentEpisodeId(v ?? '')}
              required
              error={!addAssessmentEpisodeId && addAssessmentForm.selectedQuestionnaireId ? 'Episode of Care is required' : undefined}
            />
          ) : (
            <Text size="sm" c="orange.7">
              No accessible Episodes of Care. Create or request access to an episode before taking an assessment.
            </Text>
          )}

          <Select
            label="Assessment type"
            placeholder="Select a scored measure..."
            data={measuresTemplateSelectData}
            value={addAssessmentForm.selectedQuestionnaireId || null}
            onChange={(value) =>
              setAddAssessmentForm((prev) => ({ ...prev, selectedQuestionnaireId: value ?? '' }))
            }
            searchable
            required
          />

          <Group justify="flex-end" mt="md" gap="xs">
            <Button variant="default" onClick={() => setAddAssessmentModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleTakeNowAssessment}
              loading={addAssessmentLoading}
              disabled={!addAssessmentForm.selectedQuestionnaireId || (!false && !addAssessmentEpisodeId)}
            >
              Take Now
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={modifyModalOpen}
        onClose={handleCloseModifyModal}
        title="Modify schedule"
        centered
        size="lg"
        closeOnClickOutside={!modifyLoading}
        trapFocus
      >
        {modifyingAssignment && (() => {
          const computedNextSend = isRecurringFrequency(modifyAssignmentForm.frequency as AssessmentFrequency)
            ? calculateNextAssessmentSendDate(
                dateInputValueToDateOnly(new Date()),
                modifyAssignmentForm.frequency as AssessmentFrequency,
                modifyAssignmentForm.endDate || undefined,
              )
            : null;
          return (
            <Stack gap="md">
              <Text size="sm" c="gray.6">
                Update the frequency or end date for this scheduled assessment.
              </Text>
              <Card withBorder p="md" bg="gray.0">
                <Group grow>
                  <div>
                    <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>Type</Text>
                    <Text size="sm">{modifyingAssignment.type}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>Last submission</Text>
                    <Text size="sm">{formatDate(modifyingAssignment.lastSubmission)}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>Score</Text>
                    <Text size="sm">{modifyingAssignment.score}/{modifyingAssignment.maxScore}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>Status</Text>
                    <Badge size="sm" variant="light" color={getStatusColor(modifyingAssignment.status)}>
                      {formatStatusText(modifyingAssignment.status)}
                    </Badge>
                  </div>
                </Group>
              </Card>
              <Select
                label="Recurring frequency"
                data={FREQUENCY_SELECT_DATA}
                value={modifyAssignmentForm.frequency}
                onChange={(value) =>
                  setModifyAssignmentForm((prev) => ({ ...prev, frequency: value ?? prev.frequency }))
                }
              />
              <DatePickerInput
                label="End date (optional)"
                placeholder="No end date"
                value={modifyAssignmentForm.endDate ? new Date(modifyAssignmentForm.endDate + 'T00:00:00') : null}
                onChange={(v) =>
                  setModifyAssignmentForm((prev) => ({
                    ...prev,
                    endDate: dateInputValueToDateOnly(v),
                  }))
                }
                leftSection={<IconCalendarEvent size={16} />}
                minDate={new Date()}
                clearable
              />
              <div>
                <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>Next send (estimated)</Text>
                <Text size="sm">
                  {computedNextSend
                    ? formatDate(computedNextSend)
                    : modifyAssignmentForm.frequency === 'Once'
                      ? 'One-time (no recurrence)'
                      : 'No more sends — end date reached or invalid frequency'}
                </Text>
              </div>
              <Textarea
                label="Instructions / notes for client"
                placeholder="Optional notes"
                value={modifyAssignmentForm.notes}
                onChange={(e) =>
                  setModifyAssignmentForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                minRows={2}
              />
              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={handleCloseModifyModal} disabled={modifyLoading}>
                  Cancel
                </Button>
                <Button onClick={handleSaveModifyAssignment} loading={modifyLoading}>
                  Save changes
                </Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* Stop schedule confirmation */}
      <Modal
        opened={!!stopScheduleConfirmRow}
        onClose={() => setStopScheduleConfirmRow(null)}
        title="Stop schedule?"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Stop future <Text span fw={600}>{stopScheduleConfirmRow?.type}</Text> sends?
            {' '}Completed assessments already in the log will remain.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setStopScheduleConfirmRow(null)}>
              Cancel
            </Button>
            <Button color="red" loading={stopScheduleLoading} onClick={handleConfirmStopSchedule}>
              Stop schedule
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Cancel assessment confirmation */}
      <Modal
        opened={!!cancelAssessmentConfirm}
        onClose={() => {
          if (!cancelAssessmentLoading) {
            setCancelAssessmentConfirm(null);
          }
        }}
        title="Cancel assessment?"
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Cancel <Text span fw={600}>{cancelAssessmentConfirm?.assessmentType}</Text>
            {cancelAssessmentConfirm?.administeredBy
              ? <> for this client (assigned by {cancelAssessmentConfirm.administeredBy})</>
              : null}
            {cancelAssessmentConfirm?.dueDate
              ? <> — due {formatDate(cancelAssessmentConfirm.dueDate)}</>
              : null}
            ?
          </Text>
          <Text size="sm" c="dimmed">
            The assignment will no longer be available for completion. Cancellation remains in the
            assessment history for auditing. Linked Encounter and ChargeItem will be cancelled /
            marked not-billable.
          </Text>
          <Textarea
            label="Cancellation reason"
            value={cancelAssessmentReason}
            onChange={(e) => setCancelAssessmentReason(e.target.value)}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              disabled={cancelAssessmentLoading}
              onClick={() => {
                setCancelAssessmentConfirm(null);
              }}
            >
              Keep assignment
            </Button>
            <Button
              variant="light"
              loading={cancelAssessmentLoading}
              onClick={() => void handleCancelAssessment(true)}
            >
              Cancel &amp; assign replacement
            </Button>
            <Button
              color="red"
              loading={cancelAssessmentLoading}
              onClick={() => void handleCancelAssessment(false)}
            >
              Cancel assessment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
